import React from 'react';
import { IonIcon } from '@ionic/react';
import { calendarOutline, flagOutline, golfOutline, peopleOutline, personOutline, ribbonOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { GameSetup } from './types';

interface GameOverviewProps {
  gameData: Partial<GameSetup>;
  onStartGame: () => void;
  onBack: () => void;
}

const GameOverview: React.FC<GameOverviewProps> = ({ gameData, onStartGame, onBack }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col h-full p-4">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4"
      >
        <h2 className="text-xl font-semibold">{t('Game Overview')}</h2>
        <p className="text-muted-foreground text-sm">
          {t('Review your game details before starting')}
        </p>
      </motion.div>

      <div className="flex-1 overflow-auto">
        <motion.div 
          className="space-y-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, staggerChildren: 0.1 }}
        >
          {/* Game Details */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
          >
            <h3 className="text-lg font-medium mb-3">{t('Game Details')}</h3>
            
            <div className="space-y-3">
              {/* Game Name */}
              <div className="flex items-start">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 mr-3">
                  <IonIcon icon={golfOutline} className="size-5" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{t('Game Name')}</div>
                  <div className="font-medium">{gameData.name || t('Unnamed Game')}</div>
                </div>
              </div>
              
              {/* Course */}
              {gameData.course && (
                <div className="flex items-start">
                  <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 mr-3">
                    <IonIcon icon={flagOutline} className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{t('Course')}</div>
                    <div className="font-medium">{gameData.course.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{gameData.course.location}</div>
                  </div>
                </div>
              )}
              
              {/* Date */}
              {gameData.date && (
                <div className="flex items-start">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mr-3">
                    <IonIcon icon={calendarOutline} className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{t('Date')}</div>
                    <div className="font-medium">{new Date(gameData.date).toLocaleDateString()}</div>
                  </div>
                </div>
              )}
              
              {/* Format */}
              {gameData.format && (
                <div className="flex items-start">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 mr-3">
                    <IonIcon icon={ribbonOutline} className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{t('Game Format')}</div>
                    <div className="font-medium">
                      {gameData.format === 'strokeplay' && t('Stroke Play')}
                      {gameData.format === 'stableford' && t('Stableford')}
                      {gameData.format === 'matchplay' && t('Match Play')}
                      {gameData.format === 'skins' && t('Skins')}
                      {gameData.format === 'bestball' && t('Best Ball')}
                      {gameData.format === 'scramble' && t('Scramble')}
                      {gameData.format === 'alternate' && t('Alternate Shot')}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Holes & Starting Hole */}
              <div className="flex items-start">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 mr-3">
                  <IonIcon icon={flagOutline} className="size-5" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{t('Course Setup')}</div>
                  <div className="font-medium">
                    {gameData.holes} {t('holes')}
                    {gameData.startingHole && gameData.startingHole !== 1 && (
                      <span> • {t('Starting at hole')} {gameData.startingHole}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Players */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium">{t('Players')}</h3>
              <div className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-xs font-medium">
                {gameData.players?.length || 0}
              </div>
            </div>
            
            <div className="space-y-3">
              {gameData.players?.map((player) => (
                <div key={player.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3">
                      <IonIcon icon={personOutline} className="text-blue-600 dark:text-blue-400 size-4" />
                    </div>
                    <div>
                      <div className="font-medium">{player.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center flex-wrap">
                        {player.handicap !== undefined && (
                          <span className="mr-2">{t('Handicap')}: {player.handicap}</span>
                        )}
                        {player.teeBox && (
                          <span className="flex items-center mr-2">
                            <span className={`inline-block w-2 h-2 rounded-full mr-1 ${
                              player.teeBox === 'white' ? 'bg-white border border-gray-300' :
                              player.teeBox === 'yellow' ? 'bg-yellow-400' :
                              player.teeBox === 'red' ? 'bg-red-500' :
                              'bg-blue-500'
                            }`}></span>
                            {t(player.teeBox.charAt(0).toUpperCase() + player.teeBox.slice(1))}
                          </span>
                        )}
                        {player.startingHole && player.startingHole !== gameData.startingHole && (
                          <span>{t('Hole')}: {player.startingHole}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          
          {/* Advanced Settings */}
          {(gameData.contests || gameData.teeOptions || gameData.useHandicaps !== undefined) && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
            >
              <h3 className="text-lg font-medium mb-3">{t('Advanced Settings')}</h3>
              
              <div className="space-y-3">
                {/* Contests */}
                {gameData.contests && (
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                      <IonIcon icon={flagOutline} className="mr-1 text-orange-500" />
                      {t('Contests')}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {gameData.contests.longestDrive && (
                        <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-full text-xs">
                          {t('Longest Drive')}
                        </span>
                      )}
                      {gameData.contests.closestToPin && (
                        <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-full text-xs">
                          {t('Closest to Pin')}
                        </span>
                      )}
                      {!gameData.contests.longestDrive && !gameData.contests.closestToPin && (
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {t('No contests selected')}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Tee Options */}
                {gameData.teeOptions && (
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                      <IonIcon icon={golfOutline} className="mr-1 text-blue-500" />
                      {t('Default Tee')}
                    </div>
                    <div className="flex items-center">
                      <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
                        gameData.teeOptions.default === 'white' ? 'bg-white border border-gray-300' :
                        gameData.teeOptions.default === 'yellow' ? 'bg-yellow-400' :
                        gameData.teeOptions.default === 'red' ? 'bg-red-500' :
                        'bg-blue-500'
                      }`}></span>
                      <span className="text-sm">
                        {t(gameData.teeOptions.default.charAt(0).toUpperCase() + gameData.teeOptions.default.slice(1))}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Handicaps */}
                {gameData.useHandicaps !== undefined && (
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                      <IonIcon icon={ribbonOutline} className="mr-1 text-purple-500" />
                      {t('Handicaps')}
                    </div>
                    <div className="text-sm">
                      {gameData.useHandicaps
                        ? <span className="text-green-600 dark:text-green-400">{t('Using handicaps')}</span>
                        : <span className="text-gray-500 dark:text-gray-400">{t('Not using handicaps')}</span>
                      }
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
      
      <div className="mt-auto pt-4 grid grid-cols-2 gap-3">
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          onClick={onBack}
          className="py-3 px-4 rounded-xl font-medium bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        >
          {t('Back')}
        </motion.button>
        
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          onClick={onStartGame}
          className="py-3 px-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium flex items-center justify-center space-x-2 transition-colors"
        >
          <IonIcon icon={golfOutline} className="size-5" />
          <span>{t('Start Game')}</span>
        </motion.button>
      </div>
    </div>
  );
};

export default GameOverview;
